/**
 * Main-thread client for the tile-parse Web Worker.
 *
 * Wraps a Worker with request-ID correlation so concurrent tile parse
 * requests (up to 9 tiles start simultaneously during initial load) resolve
 * independently. Also provides reconstruction helpers that convert the
 * worker's flat typed arrays back into live PathNode / VolumeGrid objects on
 * the main thread in O(N) — no binary parsing occurs here.
 */

import { vec3 } from 'gl-matrix';
import { PathNode } from '../navigation-mesh/PathNode';
import { VolumeGrid, VolgridScanResult } from '../navigation-mesh/VolumeGrid';
import { NavmeshWorkerResult, VolgridWorkerResult } from './TileParseWorker';

export type { NavmeshWorkerResult, VolgridWorkerResult };

// --- Reconstruction helpers ---

/**
 * Reconstruct PathNode objects from a worker result. O(N+E) — pure array
 * reads, no binary parsing. Direct neighbour push (not addNeighbour) is safe
 * because the worker serialised the already-bidirectional neighbour lists
 * produced by NavigationMeshReader.
 */
export function reconstructNavmeshNodes(result: NavmeshWorkerResult): PathNode[] {
  const { nodeCount, bounds, ids, refData, refLengths, neighborCounts, neighborIndices } = result;
  const decoder = new TextDecoder();
  const nodes: PathNode[] = new Array(nodeCount);

  let refOff = 0;
  for (let i = 0; i < nodeCount; i++) {
    const min = vec3.fromValues(bounds[i * 6], bounds[i * 6 + 1], bounds[i * 6 + 2]);
    const max = vec3.fromValues(bounds[i * 6 + 3], bounds[i * 6 + 4], bounds[i * 6 + 5]);
    const node = new PathNode(min, max);
    node.id = ids[i];
    const refLen = refLengths[i];
    if (refLen > 0) {
      node.reference = decoder.decode(refData.subarray(refOff, refOff + refLen));
    }
    refOff += refLen;
    nodes[i] = node;
  }

  let nOff = 0;
  for (let i = 0; i < nodeCount; i++) {
    const count = neighborCounts[i];
    const node = nodes[i];
    for (let j = 0; j < count; j++) {
      const ni = neighborIndices[nOff++];
      if (ni >= 0) node.neighbours.push(nodes[ni]);
    }
  }

  return nodes;
}

/** Reconstruct a VolumeGrid from the worker's transferred buffer + scan result. */
export function reconstructVolumeGrid(result: VolgridWorkerResult): VolumeGrid {
  return VolumeGrid.fromScan(result.buffer, result.scanResult);
}

// --- Worker client ---

type PendingRequest = { resolve: (data: unknown) => void; reject: (err: Error) => void };

/** Minimal interface so tests can inject a mock without a real Worker. */
export interface WorkerLike {
  postMessage(message: unknown, transfer: ArrayBuffer[]): void;
  onmessage: ((e: { data: unknown }) => void) | null;
  onerror: ((e: { message?: string }) => void) | null;
  terminate(): void;
}

export class TileParseWorkerClient {
  private nextId = 0;
  private pending = new Map<number, PendingRequest>();

  constructor(private readonly worker: WorkerLike) {
    worker.onmessage = (e: { data: unknown }) => {
      const msg = e.data as { reqId: number; type: string } & Record<string, unknown>;
      const entry = this.pending.get(msg.reqId);
      if (!entry) return;
      this.pending.delete(msg.reqId);
      entry.resolve(msg);
    };
    worker.onerror = (e: { message?: string }) => {
      const err = new Error(e.message ?? 'TileParseWorker error');
      for (const entry of this.pending.values()) entry.reject(err);
      this.pending.clear();
    };
  }

  parseNavmesh(buffer: ArrayBuffer): Promise<NavmeshWorkerResult> {
    return new Promise((resolve, reject) => {
      const reqId = this.nextId++;
      this.pending.set(reqId, { resolve: resolve as (d: unknown) => void, reject });
      this.worker.postMessage({ reqId, type: 'parseNavmesh', buffer }, [buffer]);
    });
  }

  parseVolgrid(buffer: ArrayBuffer): Promise<VolgridWorkerResult> {
    return new Promise((resolve, reject) => {
      const reqId = this.nextId++;
      this.pending.set(reqId, { resolve: resolve as (d: unknown) => void, reject });
      this.worker.postMessage({ reqId, type: 'parseVolgrid', buffer }, [buffer]);
    });
  }

  terminate(): void {
    const err = new Error('TileParseWorkerClient terminated');
    for (const entry of this.pending.values()) entry.reject(err);
    this.pending.clear();
    this.worker.terminate();
  }
}
